<template>
    <v-container class="plant-form" v-if="plant">
        <v-form ref="form" v-if="authenticated">
            <label class="font-weight-thin headline">{{ $t('key.properties') }}</label>

            <v-row>
                <v-col cols="12" md="4" lg="4">
                    <v-text-field :label="$t('key.neophyteName')" v-model="plant.name" :disabled="!authenticated"></v-text-field>
                </v-col>
                <v-col cols="12"  md="4" lg="4">
                    <v-text-field class="font-italic" :label="$t('key.scientificName')" v-model="plant.scientificName" :disabled="!authenticated"></v-text-field>
                </v-col>
                <v-col cols="12" md="4" lg="4">
                    <v-select label="Warnhinweis" name="risk" v-model="plant.riskLevel" :items="riskLevel" :disabled="!authenticated">
                    </v-select>
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <v-combobox :label="$t('key.mixUps')" v-model="plant.mixUps" multiple chips deletable-chips :disabled="!authenticated">
                        <template v-slot:selection="data">
                            <v-chip
                                :key="JSON.stringify(data.item)"
                                @click="openSite(data.item)"
                                class='with_link'
                            >
                                {{data.item}}
                                <v-icon>
                                    mdi-open-in-new
                                </v-icon>
                            </v-chip>
                        </template>
                    </v-combobox>
                </v-col>
            </v-row>

            <label class="font-weight-thin headline">{{ $t('key.images') }}</label>

            <div class="headline-content">
                    <ImageEditor v-if="authenticated" ref="imageeditor" imageType="plant" :refId="plantId"  maxFiles="5"></ImageEditor>
                    <ImageCarousel v-else :refId="plantId"></ImageCarousel>
            </div>

            <label class="font-weight-thin headline">{{ $t('key.briefing') }}</label>

            <v-container class="briefing" :class="{'reader-view': !authenticated, 'edit-view': authenticated}">
                <v-row>
                    <v-col class="editor" cols="12" md="6" lg="5" v-if="authenticated">
                        <label class="font-weight-thin">{{ $t('key.editor') }}</label>
                        <v-textarea v-model="briefing" outlined rows="18" auto-grow :disabled="!authenticated"></v-textarea>
                    </v-col>
                    <v-col class="preview" :md="authenticated ? 6 : 12" :lg="authenticated ? 5 : 12" :offset-lg="authenticated ? 2 : 0">
                        <label class="font-weight-thin" v-if="authenticated">{{ $t('key.preview') }}</label>
                        <div v-html="compiledMarkdown"></div>
                    </v-col>
                </v-row>
            </v-container>

            <label class="font-weight-thin headline" v-if="authenticated">{{ $t('key.color') }}</label>

            <v-row v-if="authenticated">
                <v-col>
                    <v-color-picker v-model="plant.color" label="Color" hide-canvas @update:color="colorChange" :disabled="!authenticated"></v-color-picker>
                </v-col>
            </v-row>

            <label class="font-weight-thin headline" v-if="authenticated">{{ $t('key.map') }}</label>

            <v-row v-if="authenticated">
                <v-col>
                    <v-checkbox v-model="plant.mapMarker" :label="$t('key.mapMarker')"></v-checkbox>
                </v-col>
                <v-col>
                    <v-checkbox v-model="plant.mapCircle" :label="$t('key.mapCircle')"></v-checkbox>
                </v-col>
                <v-col>
                    <v-checkbox v-model="plant.mapPolygon" :label="$t('key.mapPolygon')"></v-checkbox>
                </v-col>
            </v-row>
        </v-form>

        <div class="context" v-else>
            <PlantContext :plant="plant" :show-images="true"></PlantContext>
        </div>

        <v-row>
            <v-spacer></v-spacer>
            <v-btn dark class="green lighten-1 ma-2" min-width="100px" @click="onSubmit" :disabled="!authenticated">{{ $t('app.save') }}</v-btn>
            <v-btn class="ma-2" min-width="100px" @click="onCancel">{{ $t('app.cancel') }}</v-btn>
        </v-row>
    </v-container>
</template>

<script>
    import marked from 'marked'

    import ImageEditor from '../../base/ImageEditorDirect'
    import ImageCarousel from '../../base/ImageCarousel'
    import PlantContext from '@/components/detail/plant/PlantContext'
    import {Roles} from '@/components/_helpers'

    export default {
        name: 'PlantForm',
        props: ['plantId'],
        components: {
            PlantContext,
            ImageCarousel,
            ImageEditor
        },
        data: function() {
            return {
                briefing: '', // init in mounted() hook; not set to null otherwise marked() throw exception
                imageIds: [],
                riskLevel: []
            }
        },
        computed: {
            plant() {
                let plant = this.findPlant(this.plantId)
                // Initialize data for update- or create-mode
                return plant ? plant : { uuid: this.plantId }
            },
            compiledMarkdown() {
                let htmlCode = marked(this.briefing)
                return htmlCode
            },
            i18n() {
                return this.i18n.$t('key.properties')
            },
            authenticated() {
                let userProfile = this.$store.getters.userProfile

                // Only an authenticated user with SUPERADMIN role assigned, is
                // allowed to manage plants
                return this.$store.getters.isAuthenticated
                    && userProfile && (userProfile.role === Roles.ROLE_1.value)
            }
        },
        methods: {
            findPlant(plantId) {
                return this.$store.state.plant.plantList.find(plant =>
                    plant.uuid == plantId
                )
            },
/*            imageEditorUpload(imageId) {
                this.log(`Image uploaded: ${imageId}`)
                this.imageIds.push(imageId)
            },
            imageEditorDelete(imageId) {
            this.log(`Image delete: ${imageId}`)
            this.imageIds.splice(this.imageIds.indexOf(imageId),1)
            },*/
            colorChange(color) {
                this.plant.color = color.hex
            },
            onSubmit() {
                this.log(`Submit clicked`)

                if (this.$refs.form.validate()) {
                    // Update briefing property
                    this.plant.briefing = this.briefing
/*
                    // Copy image references
                    if (this.imageIds && this.imageIds.length > 0) {
                        // Create & init property
                        this.plant.imageIds = []
                        // Set property
                        for (let imageId of this.imageIds) {
                            this.plant.imageIds.push(imageId)
                        }
                    }
                    else
                        this.plant.imageIds = []
*/
                    this.$refs.imageeditor.saveState();

                    this.$store.dispatch('applyPlant', this.plant)
                } else {
                    this.log(`Form invalid`)
                }

            },
            onCancel() {
                this.log(`Cancel clicked`)
                this.$router.push({name: 'Plants'})
            },
            log(text) {
                console.log(`[PlantForm] ${text}`)
            },
            openSite(url) {
                window.open('https://www.google.at/search?q=' + url, '_blank')
            }
        },
        mounted() {
            // Initialize data
            let plant = this.findPlant(this.plantId)
            // Initialize properties
            this.briefing = plant ? plant.briefing : '# Merkmale'

            this.riskLevel = [
                {
                    text: "Keine Risiken",
                    value: "low"},
                {
                    text: "mögliche Nebenwirkungen",
                    value: "warning"},
                {
                    text: "Gesundheitsgefährdend",
                    value: "critical"}
            ]
        }
    };
</script>

<style lang="scss">
    div.plant-form {
        padding: 0px;

        div.context {
            padding: 0px;

            /* PlantContext.vue */
            div.container {
                padding: 0px;
            }
        }
    }

    form {
        .headline-content {
            padding: 12px 0px;
        }

        .briefing.edit-view {
            padding: 0px;

            div.editor.col > div {
                height: 100%;
            }

            div.preview.col {
                div {
                    height: 88%;
                    padding: 5px;
                    background-color: lightgrey;
                    border: 1px solid rgba(0, 0, 0, 0.4);
                    border-radius: 4px;
                }
            }
        }

        .briefing.reader-view {
            div.preview.col {
                div {
                    height: 100%;
                    padding: 5px;
                    border: 1px solid rgba(0, 0, 0, 0.4);
                    border-radius: 4px;
                }
            }
        }
    }
    .no_link {
        cursor: default !important;
    }
</style>
